/**
 * 右键上下文菜单 UI（P0-3d B-2）：把注册表产出的菜单项渲染成浮层。
 *
 * 只负责"显示 + 回抛 action"，不碰 store——具体行为由页面 dispatchAction 落地。
 * 关闭时机：点击菜单外、Escape、滚动/resize（浮层坐标会失准，直接收）。
 * 定位：fixed 到光标处，并夹到视口内，防贴边溢出。
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { MenuAction, MenuItem } from './menuRegistry';

export interface ContextMenuProps {
  x: number;
  y: number;
  items: MenuItem[];
  onSelect: (action: MenuAction) => void;
  onClose: () => void;
}

export function ContextMenu({ x, y, items, onSelect, onClose }: ContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x, y });

  // 夹到视口内：先按光标定位，再用实测尺寸修正溢出
  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null) {
      return;
    }
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const nx = Math.min(x, window.innerWidth - w - 8);
    const ny = Math.min(y, window.innerHeight - h - 8);
    setPos({ x: Math.max(8, nx), y: Math.max(8, ny) });
  }, [x, y, items.length]);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    function dismiss(): void {
      onClose();
    }
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', dismiss);
    window.addEventListener('scroll', dismiss, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', dismiss);
      window.removeEventListener('scroll', dismiss, true);
    };
  }, [onClose]);

  return (
    <>
      {/* 透明背板：点击任意处关闭菜单 */}
      <div className="fixed inset-0 z-40" onContextMenu={(e) => { e.preventDefault(); onClose(); }} onMouseDown={onClose} />
      <div
        ref={ref}
        role="menu"
        className="fixed z-50 min-w-[144px] overflow-hidden rounded-card border border-surface-edge bg-surface-overlay py-1 shadow-[0_6px_20px_rgba(0,0,0,.5)]"
        style={{ left: pos.x, top: pos.y }}
      >
        {items.map((item) => (
          <button
            key={item.action}
            type="button"
            role="menuitem"
            className={`flex w-full items-center px-3 py-1.5 text-left text-ui transition-colors duration-[120ms] ease-out ${
              item.danger
                ? 'text-state-danger hover:bg-state-danger/15'
                : 'text-ink-secondary hover:bg-surface-raised hover:text-ink-primary'
            }`}
            onClick={() => onSelect(item.action)}
          >
            {item.label}
          </button>
        ))}
      </div>
    </>
  );
}
